import React from "react"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { Chip, TextField, Theme, alpha, makeStyles } from "@material-ui/core"
import { MaterialSymbol } from "./MaterialSymbol"
import { HourReportingRolesTranslations } from "../hooks/use-translations"
import { HourReportingRoleType, RoleSelectorOption } from "../types"

type RoleSelectorProps = {
  label: string
  roleType: HourReportingRoleType
  options: RoleSelectorOption[]
  selectedMembers: RoleSelectorOption[]
  onChange: (selectedMembers: RoleSelectorOption[]) => void
  translations: HourReportingRolesTranslations
  disabled?: boolean
}

export const RoleSelector = ({
  label,
  roleType,
  options,
  selectedMembers,
  onChange,
  translations,
  disabled,
}: RoleSelectorProps) => {
  const classes = useStyles()
  const chipClassName = roleType === "checker" ? classes.checkerChip : classes.approverChip
  const roleIconName = roleType === "checker" ? "data_check" : "verified"
  const sortedOptions = React.useMemo(() => [...options].sort((a, b) => a.name.localeCompare(b.name)), [options])

  return (
    <Autocomplete
      multiple
      size="small"
      value={selectedMembers}
      options={sortedOptions}
      disabled={disabled}
      onChange={(event, value) => onChange(value)}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, value) => option.id === value.id}
      filterSelectedOptions
      limitTags={2}
      noOptionsText={translations.noOptionsText}
      classes={{ inputRoot: classes.roleSelectorInputRoot }}
      popupIcon={<MaterialSymbol name="add" className={classes.roleSelectorPopupIcon} />}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            size="small"
            label={option.name}
            className={`${classes.roleChip} ${chipClassName}`}
            icon={<MaterialSymbol name={roleIconName} className={classes.roleChipIcon} />}
            deleteIcon={<MaterialSymbol name="close" className={classes.roleChipDeleteIcon} />}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedMembers.length ? undefined : translations.emptyRolePlaceholder}
          variant="outlined"
        />
      )}
    />
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  roleSelectorInputRoot: {
    alignItems: "flex-start",
    borderRadius: 6,
    paddingTop: `${theme.spacing(0.75)}px !important`,
    paddingBottom: `${theme.spacing(0.75)}px !important`,
    paddingLeft: `${theme.spacing(0.75)}px !important`,
    "& .MuiAutocomplete-input": {
      minWidth: "140px !important",
      fontSize: 14,
    },
    "& span.MuiAutocomplete-tag": {
      marginTop: 6,
    },
  },
  roleChip: {
    borderRadius: 6,
    borderStyle: "solid",
    borderWidth: 1,
    fontSize: 12,
    fontWeight: 700,
    height: 26,
    lineHeight: 1,
    margin: theme.spacing(0.25, 0.5, 0.25, 0),
    maxWidth: "100%",
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.25),
    },
    "& .MuiChip-deleteIcon": {
      margin: theme.spacing(0, 0.5, 0, 0.25),
    },
  },
  checkerChip: {
    color: theme.palette.info.dark,
    borderColor: alpha(theme.palette.info.main, 0.35),
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    "& .MuiChip-deleteIcon": {
      color: alpha(theme.palette.info.main, 0.78),
      "&:hover": {
        color: theme.palette.info.dark,
      },
    },
  },
  approverChip: {
    color: theme.palette.success.dark,
    borderColor: alpha(theme.palette.success.main, 0.35),
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    "& .MuiChip-deleteIcon": {
      color: alpha(theme.palette.success.main, 0.78),
      "&:hover": {
        color: theme.palette.success.dark,
      },
    },
  },
  roleChipIcon: {
    color: "inherit",
    fontSize: 18,
    marginLeft: 6,
    marginRight: -2,
    fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 18",
  },
  roleChipDeleteIcon: {
    fontSize: 18,
    fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 18",
  },
  roleSelectorPopupIcon: {
    fontSize: 20,
    fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 20",
  },
}))
